// @ts-nocheck
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser, SignInButton, SignUpButton } from '@clerk/clerk-react';
import IconBar from './IconBar';
import ContextPanel from './ContextPanel';
import WorkflowTileSelector from '../ui/WorkflowTileSelector';
import { useLocalization, type Language } from '../../config/localization';
import { useSidebar } from '../../contexts/SidebarContext';
import { getContextFromUrl, getBestPathForContext, isPathInContext, hasSubNavigation, DEFAULT_CONTEXT_PATHS } from '../../utils/contextMapping';
import { getWorkflowUsageHistory } from '../../utils/workflowHistory';

export type WorkMode = 'image' | 'video' | 'audio' | 'gallery' | 'text' | '3d';

interface MainSidebarLayoutProps {
  children: React.ReactNode;
  loading?: boolean;
  selectedLanguage: Language;
  onLanguageChange: (language: Language) => void;
  selectedMode?: WorkMode;
  onModeChange?: (mode: WorkMode) => void;
  className?: string;
  // Gallery filter props
  galleryFilters?: any;
  onGalleryFiltersChange?: (filters: any) => void;
  // Multi section: model picker rendered inside the context panel. The page owns
  // the selection state, so it passes the ready element down (same pattern as
  // the gallery filter props above).
  multiSidebarPanel?: React.ReactNode;
  // Multi section: cost + Generate, pinned to the bottom of the context panel
  // (where every other section keeps its Generate button).
  multiSidebarFooter?: React.ReactNode;
}

export default function MainSidebarLayout({
  children,
  loading = false,
  selectedLanguage,
  onLanguageChange,
  selectedMode = 'image',
  onModeChange = () => {},
  className = '',
  galleryFilters,
  onGalleryFiltersChange,
  multiSidebarPanel,
  multiSidebarFooter
}: MainSidebarLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoaded } = useUser();
  const t = useLocalization(selectedLanguage);
  const { activeContext, setActiveContext, isContextPanelCollapsed, getSubContextState, setSubContextState } = useSidebar();

  // Track programmatic navigation to avoid conflicts
  const programmaticNavigationRef = useRef(false);
  const lastNavTargetRef = useRef<string | null>(null);

  // Workflow Tile Selector Modal State (managed at layout level to render outside sidebar)
  const [isTileSelectorOpen, setIsTileSelectorOpen] = useState(false);
  const [tileSelectorWorkflows, setTileSelectorWorkflows] = useState<any[]>([]);
  const [tileSelectorSelectedId, setTileSelectorSelectedId] = useState<string | null>(null);
  const [tileSelectorOnSelect, setTileSelectorOnSelect] = useState<((id: string) => void) | null>(null);

  // Function to open tile selector from child components
  const openTileSelector = (workflows: any[], selectedId: string | null, onSelect: (id: string) => void) => {
    setTileSelectorWorkflows(workflows);
    setTileSelectorSelectedId(selectedId);
    setTileSelectorOnSelect(() => onSelect); // Wrap in function to avoid calling it immediately
    setIsTileSelectorOpen(true);
  };

  const closeTileSelector = () => {
    setIsTileSelectorOpen(false);
  };


  // Auto-set context based on current route and mode
  useEffect(() => {
    console.log('🔄 AUTO-DETECTION useEffect triggered:', {
      pathname: location.pathname,
      activeContext,
      programmaticNavigation: programmaticNavigationRef.current,
      lastNavTarget: lastNavTargetRef.current
    });

    // If we initiated navigation ourselves, wait until URL matches target before clearing flag
    if (programmaticNavigationRef.current) {
      if (lastNavTargetRef.current && location.pathname === lastNavTargetRef.current) {
        console.log('✅ PROGRAMMATIC NAVIGATION COMPLETED');
        programmaticNavigationRef.current = false;
        lastNavTargetRef.current = null;
      } else {
        console.log('⏭️ WAITING FOR NAVIGATION TO COMPLETE');
        return;
      }
    }

    const detectedContext = getContextFromUrl(location.pathname);
    console.log('🔍 Detected context:', detectedContext, 'vs current:', activeContext);

    if (detectedContext && detectedContext !== activeContext) {
      console.log('🎯 SETTING NEW CONTEXT:', detectedContext);
      setActiveContext(detectedContext);
      
      // Save the current path as sub-context state for contexts that support it
      if (hasSubNavigation(detectedContext)) {
        setSubContextState(detectedContext, location.pathname);
      }
    } else {
      console.log('✋ NO CONTEXT CHANGE NEEDED');
    }
  }, [location.pathname, activeContext]);

  // Handle context changes and navigation
  const handleContextChange = (context: typeof activeContext) => {
    console.log('🎮 HANDLE CONTEXT CHANGE called:', {
      requestedContext: context,
      currentContext: activeContext,
      pathname: location.pathname,
      isAlreadyInContext: isPathInContext(location.pathname, context)
    });
    
    // Prevent redundant context changes
    if (context === activeContext && isPathInContext(location.pathname, context)) {
      console.log('🚫 REDUNDANT CONTEXT CHANGE - SKIPPING');
      return;
    }
    
    console.log('✅ PROCEEDING WITH CONTEXT CHANGE');
    setActiveContext(context);
    
    // Handle navigation based on context type
    switch (context) {
      case 'admin':
      case 'organization':
        // For contexts with sub-navigation, navigate if not already in context
        if (!isPathInContext(location.pathname, context)) {
          const savedSubPath = getSubContextState(context);
          const targetPath = getBestPathForContext(context, savedSubPath, location.pathname);
          programmaticNavigationRef.current = true;
          lastNavTargetRef.current = targetPath;
          navigate(targetPath);
        }
        break;
        
      case 'image':
        // Navigate to image page
        if (!isPathInContext(location.pathname, context)) {
          const targetPath = DEFAULT_CONTEXT_PATHS[context];
          programmaticNavigationRef.current = true;
          lastNavTargetRef.current = targetPath;
          navigate(targetPath);
        }
        if (onModeChange) onModeChange('image');
        break;
        
      case 'gallery':
        // Navigate to gallery page
        if (!isPathInContext(location.pathname, context)) {
          const targetPath = DEFAULT_CONTEXT_PATHS[context];
          programmaticNavigationRef.current = true;
          lastNavTargetRef.current = targetPath;
          navigate(targetPath);
        }
        if (onModeChange) onModeChange('gallery');
        break;
        
      case 'video':
        // Navigate to video page
        if (!isPathInContext(location.pathname, context)) {
          const targetPath = DEFAULT_CONTEXT_PATHS[context];
          programmaticNavigationRef.current = true;
          lastNavTargetRef.current = targetPath;
          navigate(targetPath);
        }
        if (onModeChange) onModeChange('video');
        break;

      case 'audio':
        // Navigate to audio page
        if (!isPathInContext(location.pathname, context)) {
          const targetPath = DEFAULT_CONTEXT_PATHS[context];
          programmaticNavigationRef.current = true;
          lastNavTargetRef.current = targetPath;
          navigate(targetPath);
        }
        break;

      case 'text':
        // Navigate to text page
        if (!isPathInContext(location.pathname, context)) {
          const targetPath = DEFAULT_CONTEXT_PATHS[context];
          programmaticNavigationRef.current = true;
          lastNavTargetRef.current = targetPath;
          navigate(targetPath);
        }
        break;

      case '3d':
        // Navigate to the 3D generation page
        if (!isPathInContext(location.pathname, context)) {
          const targetPath = DEFAULT_CONTEXT_PATHS[context];
          programmaticNavigationRef.current = true;
          lastNavTargetRef.current = targetPath;
          navigate(targetPath);
        }
        break;

      case 'multi':
        // Navigate to the Multi (parallel multi-model) page
        if (!isPathInContext(location.pathname, context)) {
          const targetPath = DEFAULT_CONTEXT_PATHS[context];
          programmaticNavigationRef.current = true;
          lastNavTargetRef.current = targetPath;
          navigate(targetPath);
        }
        break;

      case 'settings':
      case 'help':
        // Navigate to settings and help pages
        if (!isPathInContext(location.pathname, context)) {
          const targetPath = DEFAULT_CONTEXT_PATHS[context];
          programmaticNavigationRef.current = true;
          lastNavTargetRef.current = targetPath;
          navigate(targetPath);
        }
        break;
        
      default:
        break;
    }
  };

  if (!isLoaded || loading) {
    return (
      <div className="min-h-screen bg-crt-bg bg-cover bg-center text-white font-gaming flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className={`min-h-screen bg-crt-bg bg-cover bg-center text-white font-gaming ${className}`}>
        {/* Icon Bar (Left - 64px) */}
        <IconBar
          selectedLanguage={selectedLanguage}
          activeContext={activeContext}
          onContextChange={handleContextChange}
        />

        {/* Context Panel (Left - 280px after icon bar) */}
        <ContextPanel
          activeContext={activeContext}
          selectedLanguage={selectedLanguage}
          onLanguageChange={onLanguageChange}
          isUnauthenticated={true}
          galleryFilters={galleryFilters}
          onGalleryFiltersChange={onGalleryFiltersChange}
          onOpenTileSelector={openTileSelector}
        />

        {/* Main Content Area for Authentication */}
        <main className={`${isContextPanelCollapsed ? 'ml-20' : 'ml-[400px]'} min-h-screen flex flex-col transition-all duration-300`}>
          <div className="flex items-center justify-center h-full">
            <div className="text-center py-16">
              <div className="text-8xl mb-6">🔐</div>
              <h2 className="text-3xl font-bold mb-4">{t.auth?.required || 'Требуется авторизация'}</h2>
              <p className="text-xl text-purple-200 mb-8">
                {t.auth?.loginRequired || 'Для использования приложения необходимо войти в систему'}
              </p>
              
              {/* Login/Signup Buttons */}
              <div className="flex justify-center space-x-4">
                <SignInButton mode="modal">
                  <button className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg text-lg font-medium transition-colors">
                    {t.signIn || 'Войти'}
                  </button>
                </SignInButton>
                <SignUpButton mode="modal">
                  <button className="border border-purple-500 text-purple-300 hover:bg-purple-500/10 px-6 py-3 rounded-lg text-lg font-medium transition-colors">
                    {t.signUp || 'Регистрация'}
                  </button>
                </SignUpButton>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-crt-bg bg-cover bg-center text-white font-gaming ${className}`}>
      {/* Icon Bar (Left - 64px) */}
      <IconBar
        selectedLanguage={selectedLanguage}
        activeContext={activeContext}
        onContextChange={handleContextChange}
      />

      {/* Context Panel (Left - 280px after icon bar) */}
      <ContextPanel
        activeContext={activeContext}
        selectedLanguage={selectedLanguage}
        onLanguageChange={onLanguageChange}
        galleryFilters={galleryFilters}
        onGalleryFiltersChange={onGalleryFiltersChange}
        onOpenTileSelector={openTileSelector}
        multiSidebarPanel={multiSidebarPanel}
        multiSidebarFooter={multiSidebarFooter}
      />

      {/* Main Content Area - re-mount on path change to ensure fresh content */}
      <main key={location.pathname} className={`${isContextPanelCollapsed ? 'ml-20' : 'ml-[400px]'} min-h-screen flex flex-col transition-all duration-300`}>
        {children}
      </main>

      {/* Workflow Tile Selector Modal - Rendered at layout level to appear centered over entire app */}
      <WorkflowTileSelector
        isOpen={isTileSelectorOpen}
        onClose={closeTileSelector}
        workflows={tileSelectorWorkflows}
        selectedWorkflowId={tileSelectorSelectedId}
        onSelect={(workflowId) => {
          // Don't save to history here - only save on actual generation
          if (tileSelectorOnSelect) {
            tileSelectorOnSelect(workflowId);
          }
          closeTileSelector();
        }}
        language={selectedLanguage}
        workflowUsageHistory={getWorkflowUsageHistory()}
      />
    </div>
  );
}